<template>
    <div>
        <ElementsTooltipSingleton
            :ref="
                (el) => {
                    loadTooltipElementReady(el);
                }
        " />
        <div class="flex flex-col gap-2 w-[540px]">
            <div class="font-body-sm-semibold">
                Approval process
            </div>
            <template v-for="(approveItem, index) in approvedList" :key="index">
                <div class="flex gap-2 items-center">
                    <div
                        class="rounded-04 bg-background-basic-2 p-4 flex gap-2 items-center"
                        :class="containerWidth"
                    >
                        <div class="font-caption-md-semibold w-14 shrink-0">
                            Level {{ index + 1 }}
                        </div>
                        
                        <div
                            class="w-full flex h-9 p-2 rounded-02 justify-between items-center gap-1 bg-background-basic-1 border flex-1 shrink-1 min-w-0"
                            :class="approveItem.error 
                                ? 'border-accent-red-3' 
                                : 'border-border-basic-2'"
                            style="cursor:pointer"
                            @click="openSelectApprovalPopup(index)"
                        >
                            <span
                                class="text-left font-caption-md-regular truncate flex-1"
                                :class="{ 'text-content-basic-3': !approveItem?.approverTarget?.approveType }"
                            >
                                {{ nameDisplay(approveItem) }}
                            </span>
                            <button v-show="!disabled">
                                <userIcon width="20" height="20" weight="THIN" color="text-primary-3"/>
                            </button>
                        </div>
                        <ElementsToggle 
                            labelClass="font-caption-md-regular ml-1.5 mr-1 shrink-0"
                            v-model="approveItem.autoApprovedYn" :toggleLabel="'Automatic\nApproval'"
                            :useStringValueObj="toggleYesOrNoOptions" :disabled="disabled" 
                        />
                    </div>
                    <button  
                        v-show="!disabled && index!=0" @click="deleteProcessStep(index)"
                        class="h-full" :ref=" (el) => { loadTooltip(el, () => { return 'Delete'; }); }">
                        <deleteIcon width="24" height="24" weight="THIN" color="text-content-basic-3"/>
                    </button>
                </div>
                <div
                    v-if="approveItem.error"
                    class="c_alert_message mt-1" 
                >
                    Please add a user for this approver level
                </div>
            </template>
            <DesignButton
                v-if="!disabled"
                v-show="approvedList.length < 5"
                @click="addApproveStep"
                btnSize="SM"
                btnType="SECONDARY"
                btnVariant="BORDER"
                btnText="Add"
                :class="[containerWidth, 'border-border-basic-1']"
            >
                <template #front-icon>
                    <AddIcon :width="16" :height="16" color="text-content-basic-1" weight="THIN" />
                </template>
            </DesignButton>
        </div>

        <!--Select an Approver Modal START-->
        <Teleport to="body">
            <AppPopup
                v-model="showMemberAssignment"
                name="Select an Approver"
                :noneOverflowAuto="false"
                :maxWidth="'600px'"
                @afterClose="closeSelectApprovalPopup"
            >
                <!-- Level Heads Section -->
                <div class="level-heads-section flex flex-col gap-2.5 p-5">
                    <div class="w-full flex items-center gap-2">
                        <!-- Head + Add -->
                        <div class="max-w-[240px] w-full flex items-center justify-between">
                            <div class="font-body-md-semibold">
                                Head
                            </div>
                            <DesignButton
                                btnText="Add"
                                btnSize="SM"
                                btnType="SECONDARY"
                                btnVariant="BORDER"
                                :btnDisabled="false"
                                @click="addHead"
                            >
                                <template #front-icon>
                                    <AddIcon :width="16" :height="16" color="text-content-basic-1" weight="REGULAR" />
                                </template>
                            </DesignButton>
                        </div>

                        <!-- Head Add error message -->
                        <p v-if="showAddHeadError" class="c_alert_message">
                            You can add up to Level 5.
                        </p>
                    </div>

                    <div class="w-full flex flex-col gap-1.5 font-body-sm-medium text-content-inverse-1">
                        <div
                            v-for="head in headOptions.filter(head => head.isActive)"
                            :key="head.type"
                            class="flex items-center gap-4"
                        >
                            <div
                                class="flex items-center justify-center gap-2 px-5 py-2 max-w-[240px] w-full h-[2.25rem] rounded-01 cursor-pointer"
                                :class="[
                                    head.bgClass,
                                    { 'ring-2 ring-primary-3 ring-offset-2': isSelected(head.type) }
                                ]"
                                @click="selectApprover(head.type, null, head.label)"
                            >
                                <PersonAddIcon
                                    :width="16"
                                    :height="16"
                                    color="text-content-inverse-1"
                                    weight="FILL"
                                />
                                {{ head.label }}
                            </div>

                            <CheckIcon
                                v-if="isSelected(head.type)"
                                :width="24"
                                :height="24"
                                color="text-primary-3"
                                weight="REGULAR"
                            />
                        </div>
                    </div>
                </div>

                <!--line-->
                <div class="flex justify-center px-5 mt-3 mb-7">
                    <div class="w-full h-[1px] bg-background-basic-3"></div>
                </div>

                <!--Search-->
                <div class="flex items-center px-5 gap-2">
                    <DesignDropdownSelect
                        v-model="selectedDepartment"
                        :options="departmentsList"
                        placeholder="Department"
                        :useDefaultMsg="true"
                        size="MD"
                        :width="'w-40'"
                        @change="getEmployeeListBySearch"
                    />
                    <DesignSearchInput
                        v-model="nameForSearch"
                        :options="employeeOptions"
                        width="w-full"
                        placeholder="Search name"
                        size="MD"
                        @searchEvent="getEmployeeListBySearch"
                        ref="employeeSearchBar"
                        :optionClickEvent="true"
                    />
                </div>

                <!--Search result-->
                <div class="employees_container flex-1 overflow-auto ds-scrollbar px-5 mt-4 pb-8">
                    <div class="font-body-md-semibold my-2">Employees</div>
                    <ul class="mb-2">
                        <template v-for="employee in employeeLists" :key="employee.employeeSeq">
                            <li class="px-2 py-3 bg-background-basic-1 hover:bg-background-basic-2">
                                <button
                                    @click="selectApprover('USER', employee.employeeSeq, employee.employeeName)"
                                    class="group w-full flex items-start text-left"
                                >
                                    <div class="w-full flex gap-2">
                                        <div class="self-start mt-2">
                                            <StatusCircle :status-code="employee.employeeStatus" />
                                        </div>
                                        <div class="min-w-0 w-full">
                                            <div class="font-body-md-regular"
                                                :class="{
                                                    'text-primary-3': isSelected('USER', employee.employeeSeq),
                                                    'text-content-basic-1': !isSelected('USER', employee.employeeSeq)
                                                }"
                                            >
                                                {{ employee.employeeName }}
                                            </div>
                                            <div class="font-body-sm-regular text-content-basic-2">
                                                {{ employee.departmentName || '' }}
                                            </div>
                                        </div>
                                        <div v-if="isSelected('USER', employee.employeeSeq)" class="">
                                            <CheckIcon
                                                :width="24"
                                                :height="24"
                                                color="text-primary-3"
                                                weight="REGULAR"
                                            />
                                        </div>
                                    </div>
                                </button>
                            </li>
                        </template>
                    </ul>
                    <div class="p-3 flex items-center gap-2 bg-background-basic-3 rounded-3">
                        <ErrorIcon :width="20" :height="20" color="text-content-basic-2" weight="REGULAR" />
                        <span class="font-body-sm-regular text-content-basic-2">
                            Maximum 10 employees are displayed.
                        </span>
                    </div>
                </div>
                <div class="sticky bottom-0 shrink-0 bg-background-basic-1 border-t border-border-basic-2 px-7 py-4">
                    <div class="flex items-center gap-4">
                        <p v-if="approverAddError" class="c_alert_message">
                            {{ approverAddErrorMessage }}
                        </p>
                        <DesignButton
                            btnText="Add"
                            btnSize="MD"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            class="w-[84px] ml-auto"
                            @click="addApprover"
                        />
                    </div>
                </div>
            </AppPopup>
        </Teleport>
    </div>
</template>

<style scoped>
.employees_container li+li{
    border-top: 1px solid #ececec;
}
</style>

<script>
import ElementsToggle from '@/components/elements/ElementsToggle.vue';
import { ValidateUtil } from '@/plugins/app-util.js';
import PersonAddIcon from '@/components/design-system/icon/person-add.vue';
import StatusCircle from '@/components/employee/status-circle/status-circle';
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignSearchInput from '@/components/design-system/design-search-input.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import AddIcon from '@/components/design-system/icon/add.vue';
import ErrorIcon from '@/components/design-system/icon/error.vue';
import CheckIcon from '@/components/design-system/icon/check.vue';
import userIcon from '@/components/design-system/icon/filing/user.vue';
import deleteIcon from '@/components/design-system/icon/delete.vue';

export default {
    components: {
        ElementsToggle,
        PersonAddIcon,
        StatusCircle,
        DesignDropdownSelect,
        DesignSearchInput,
        DesignButton,
        AddIcon,
        ErrorIcon,
        CheckIcon,
        userIcon,
        deleteIcon,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        containerWidth: {
            type: String,
            default: 'w-[510px]',
        },
    },
    data() {
        return {
            departmentsList: [],
            employeeOptions: [],
            departmentNameBySeq: {},
            showMemberAssignment: false,
            selectedDepartment: null,
            nameForSearch: null,
            employeeLists: [],
            approvedList: [{ autoApprovedYn: 'N', approverTarget: {} }],
            approvedIndex: null,
            toggleYesOrNoOptions: { true: 'Y', false: 'N' },
            approverAddError: false,
            approverAddErrorMessage: "",
            selectedApprover: {
                approveType: null,
                employeeSeq: null,
                employeeName: null,
            },
            headOptions: [
                { type: 'FIRST_HEAD', label: 'Level 1 Head', bgClass: 'bg-accent-red-3', isActive: true },
                { type: 'SECOND_HEAD', label: 'Level 2 Head', bgClass: 'bg-accent-lightblue-3', isActive: true },
                { type: 'THIRD_HEAD', label: 'Level 3 Head', bgClass: 'bg-accent-green-3', isActive: true },
                { type: 'FOURTH_HEAD', label: 'Level 4 Head', bgClass: 'bg-accent-yellow-3', isActive: false },
                { type: 'FIFTH_HEAD', label: 'Level 5 Head', bgClass: 'bg-accent-violet-3', isActive: false },
            ],
            showAddHeadError: false,
            scrollYBackup: 0
        };
    },
    computed: {
        // 여기에 모달이 더 있으면 || 로 추가
        isAnyPopupOpen() {
            return this.showMemberAssignment;
        },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    mounted() {
        this.getDepartmentOptions();
        this.getEmployeeOptions();
    },
    methods: {
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        // -----------------------------------------------------------------------------
        loadTooltip(ele, msgFn, options) {
            // console.log({ele});
            this.__tooltipComponent__hidden__.loadBasic(ele, msgFn, options);
        },
        loadTooltipElementReady(ele) {
            if (ele == null) return; // auto-reload, so element can be null sometime.
            this.__tooltipComponent__hidden__ = ele;
        },
        // -----------------------------------------------------------------------------
        over15CharFromFullName: ValidateUtil.over15CharFromFullName,
        openSelectApprovalPopup(index) {
            const self = this;
            self.resetApproverModalState();
            self.showMemberAssignment = true;
            self.approvedIndex = index;
        },
        closeSelectApprovalPopup() {
            const self = this;
            self.approvedIndex = null;
            self.resetApproverModalState();
            self.resetAddHeadState();
            self.showMemberAssignment = false;
        },
        resetApproverModalState() {
            const self = this;

            // 검색 조건 초기화
            self.selectedDepartment = null;
            self.nameForSearch = null;

            // 선택된 Approver 초기화
            self.selectedApprover = {
                approveType: null,
                employeeSeq: null,
                employeeName: null,
            };

            // 에러 상태 초기화
            self.resetApproverAddError();

            // 검색 결과 초기화
            self.employeeLists = [];

            // 검색창 초기화
            if (self.$refs.employeeSearchBar?.clearInput) {
                self.$refs.employeeSearchBar.clearInput();
            }
        },
        resetAddHeadState() {
            const self = this;
            self.showAddHeadError = false;
            self.headOptions.forEach((head, index) => {
                head.isActive = index < 3;
            });
        },
        resetApproverAddError() {
            const self = this;
            self.approverAddError = false;
            self.approverAddErrorMessage = '';
        },
        addApproveStep() {
            const self = this;
            if (self.approvedList.length == 5) {
                return;
            }
            self.approvedList.push({ autoApprovedYn: 'N', approverTarget: {} });
        },
        addHead() {
            const self = this;

            if (self.headOptions.filter(head => head.isActive).length == 5) {
                self.showAddHeadError = true;
                return;
            }

            const nextHeadIndex = self.headOptions.findIndex(head => !head.isActive);
            self.headOptions[nextHeadIndex].isActive = true;
        },
        getDepartmentOptions() {
            const self = this;
            const url = self.$api('uri', 'get-department');
            const json_query = JSON.stringify({
                showRootNode: true,
                menuAccessLevelCode: 'ATTENDANCE_ACCESS_LEVEL',
            });
            self.$axios.get(url, { params: { json_query } }).then((res) => {
                self.departmentsList = res.data.data.list.map((dep) => ({
                    text: dep.departmentName,
                    value: dep.departmentSeq,
                }));

                self.departmentNameBySeq = self.departmentsList.reduce(
                    (list, department) => ({
                        ...list,
                        [department.value]: department.text,
                    }),
                    {}
                );
            });
        },
        getEmployeeOptions() {
            const self = this;

            const url = self.$api('uri', 'get-employee-all');
            self.$axios
                .get(url)
                .then((res) => {
                    self.employeeOptions = res.data.data.list.map((emp) => ({ text: emp.employeeName, value: emp.employeeSeq }));
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        getEmployeeListBySearch() {
            const self = this;
            const url = self.$api('uri', 'get-employee-exclude-status-resigned');

            const searchedValues = self.$refs.employeeSearchBar.getSearchedValues();

            let json_query = {
                limit: 10,
                offset: 0,
                departmentSeq: self.selectedDepartment,
                // employeeStatus : null,
                employeeSeqList: searchedValues,
                jobTitleSeq: null,
            };
            json_query = JSON.stringify(json_query);
            self.$axios.get(url, { params: { json_query } }).then((res) => {
                self.employeeLists = res.data.data.list;
                self.employeeLists.forEach((employee) => {
                    employee.departmentName = self.departmentNameBySeq[employee.departmentSeq];
                });
            });
        },
        deleteProcessStep(processIndex) {
            const self = this;
            if (self.approvedList.length == 1) {
                delete self.approvedList[0].error;
                self.approvedList[0].approverTarget = {};
                return;
            }
            self.approvedList.splice(processIndex, 1);
        },
        selectApprover(approveType, employeeSeq, employeeName) {
            const self = this;
            self.resetApproverAddError();
            self.selectedApprover = { approveType, employeeSeq, employeeName };
        },
        isSelected(type, seq = null) {
            const self = this;
            if (!self.selectedApprover) return false;
            if (self.selectedApprover.approveType !== type) return false;
            if (type === 'USER') {
                return self.selectedApprover.employeeSeq === seq;
            }
            return true;
        },
        addApprover() {
            const self = this;
            const selectedApprover = self.selectedApprover;
            if (!selectedApprover || !selectedApprover.approveType || (selectedApprover.approveType === 'USER' && !selectedApprover.employeeSeq)) {
                self.approverAddError = true;
                self.approverAddErrorMessage = 'Please select at least one Approver.';
                return;
            }

            const duplicateLevel = self.findDuplicateLevel(selectedApprover, self.approvedIndex);
            if (duplicateLevel !== -1) {
                self.approverAddError = true;
                self.approverAddErrorMessage = `This user is already selected as Level ${duplicateLevel + 1} approver.`;
                return;
            }

            // Add approver
            const { approveType, employeeSeq, employeeName } = selectedApprover;
            self.approvedList[self.approvedIndex].approverTarget = { approveType, employeeSeq, employeeName };

            self.closeSelectApprovalPopup();
        },
        findDuplicateLevel(selected, excludeIndex) {
            if (!selected?.approveType) return -1;

            const selectType = selected.approveType;
            const selectedEmployeeSeq  = selected.employeeSeq;

            for (let i = 0; i < this.approvedList.length; i++) {
                if (i === excludeIndex) continue;

                const target = this.approvedList[i]?.approverTarget || {};
                const type = target.approveType;
                
                if (!type) continue; 
                if (selectType !== type) continue;
                if (selectType === 'USER') {
                    const employeeSeq = target.employeeSeq;
                    if (selectedEmployeeSeq !== employeeSeq) continue;
                }

                return i;
            }
            return -1;
        },
        checkValidation() {
            const self = this;
            let isValid = true;

            self.approvedList.forEach((process, index) => {
                process.approvalStageOrder = index;
                delete process.error;

                if (!process.approverTarget.approveType) {
                    process.error = true;
                    isValid = false;
                }
            });
            return isValid;
        },
        getApprovalProcessForAxios() {
            const self = this;
            const approvalProcess = self.approvedList.map((process) => {
                const {
                    approvalStageOrder,
                    autoApprovedYn,
                    approverTarget: { approveType, employeeSeq },
                } = process;
                // delete approverTarget.employeeName;
                return { autoApprovedYn, approvalStageOrder, approveType, employeeSeq };
            });
            return approvalProcess;
        },
        getApprovalListFromDb(processItem) {
            const self = this;
            if (processItem.length < 1) return [{ autoApprovedYn: true, approverTarget: {} }];

            const approvalList = processItem.map((process) => {
                const { approvalStageOrder, autoApprovedYn, approveType, employeeSeq, employeeName } = process;
                return { autoApprovedYn, approvalStageOrder, approverTarget: { approveType, employeeSeq, employeeName } };
            });
            self.approvedList = approvalList.sort((a, b) => a.approvalStageOrder - b.approvalStageOrder);
        },
        resetApprovalProcess() {
            const self = this;
            self.approvedList = [{ autoApprovedYn: 'N', approverTarget: {} }];
            self.approvedIndex = null;
        },
        nameDisplay(item){
            const approveType = item?.approverTarget?.approveType;
            if( approveType == 'USER' ){
                return item.approverTarget.employeeName;
            }
            if( approveType == 'FIRST_HEAD' ){
                return 'Level 1 Head'
            }
            if( approveType == 'SECOND_HEAD' ){
                return 'Level 2 Head'
            }
            if( approveType == 'THIRD_HEAD' ){
                return 'Level 3 Head'
            }
            if (approveType == 'FOURTH_HEAD') {
                return 'Level 4 Head'
            }
            if (approveType == 'FIFTH_HEAD') {
                return 'Level 5 Head'
            }
            return 'Select Employees';
        }
    },
};
</script>
